import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { ContextMenuWindowComponent } from '@frontendlib/components/features/popover/components/context-menu-window/context-menu-window.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import type { CanvasBridge } from '@frontendlib/boards-canvas';
import { validateSceneImport } from '@sharedlib/common/boards-scene/bridge-guards';
import { boardFrames } from '@sharedlib/common/boards-scene/scene-functions';
import { objects, type SceneDoc } from '@sharedlib/common/boards-scene/scene-model';
import { BoardsCanvasHostComponent } from '../../../../canvas/boards-canvas-host.component';
import { BoardsPresentHostComponent, type PresentFrame } from '../../../../present/boards-present-host.component';
import { frameRenderer, presentableFrames } from '../../../../present/board-present.util';
import { fitBoardToContent } from '../../../../canvas/board-fit.util';
import { isExcalidrawMobile, normalizeZoom, ZOOM_STEP, zoomPreviewTo } from '../../../../canvas/board-preview-zoom.util';
import { BoardExportMenuComponent } from '../../../../export/board-export-menu.component';
import { type Board } from '../../../../types/board';
import { BoardsDocumentApiService } from '../../../../services/boards-document-api.service';
import { BoardsEditorStore } from '../../../../services/boards-editor.store';

/** Prototype `BoardPanelRecord.tsx:50`: the layout settles before the first fit. */
export const FIT_DELAY_MS = 120;

/**
 * The Board tab of the record: the first tab, a read-only canvas under the "N structured objects · N frames" line with
 * Present and Open editor (prototype `BoardPanelRecord.tsx:68` for the Board tab body, `Boards.tsx:318` for the count
 * line). It takes the place of `app-bento-preview` in `slides-preview.component.html:183-190`. The scene is read with the
 * editor's own gateway and guard and shown by the same lazy canvas host the editor uses; nothing here saves. A late answer
 * for a board the record has left is dropped (generation fence). Download belongs to the export relay (R06).
 */
@Component({
  selector: 'app-board-record-tab',
  templateUrl: './board-record-tab.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './board-record-tab.component.less',
  imports: [BoardsCanvasHostComponent, BoardsPresentHostComponent, Button2Component, BoardExportMenuComponent, ContextMenuWindowComponent, IconComponent, PopoverDirective],
})
export class BoardRecordTabComponent {
  public readonly boardId = input.required<string>();
  public readonly title = input('Board');
  /** The board row the Download menu acts on (R06's export menu). */
  public readonly board = input<Board | null>(null);
  public readonly trashed = input(false);
  public readonly openEditor = output<void>();
  /** The counts of the loaded scene, for the record's footer (prototype `Boards.tsx:318`); null while there is none. */
  public readonly counts = output<{ objects: number; frames: number } | null>();

  private readonly api = inject(BoardsDocumentApiService);
  private readonly editor = inject(BoardsEditorStore);
  /** The guarded open of THIS board is still loading (the editor store's own `opening`): Open editor shows busy, as the empty state's button does. */
  protected readonly opening = computed(() => this.editor.opening() === this.boardId());
  private generation = 0;
  private bridge: CanvasBridge | undefined;

  protected readonly scene = signal<SceneDoc | null>(null);
  protected readonly failed = signal(false);
  protected readonly bridgeIsReady = signal(false);
  protected readonly presentFrames = signal<readonly PresentFrame[] | null>(null);
  protected readonly objectCount = computed(() => objects(this.scene() ?? { elements: [] }).length);
  protected readonly frameCount = computed(() => boardFrames(this.scene() ?? { elements: [] }).length);
  protected readonly canPresent = computed(() => this.frameCount() > 0 && this.bridgeIsReady());
  protected readonly renderFrame = frameRenderer(() => this.bridge);
  private fitTimer: ReturnType<typeof setTimeout> | null = null;
  /** The frame Excalidraw is mounted in: when it is too small for the engine's desktop layout the preview shows its own zoom controls (see `board-preview-zoom.util`). */
  private readonly frame = viewChild<ElementRef<HTMLElement>>('frame');
  protected readonly narrow = signal(false);
  /** The engine's own zoom, as its change callback last reported it (`canvasChange` `view.zoom`): the initial automatic fit, a gesture, the buttons and a board switch all arrive here. */
  private readonly zoomValue = signal(1);
  protected readonly zoomPercent = computed(() => Math.round(this.zoomValue() * 100));
  protected readonly canZoomOut = computed(() => this.zoomValue() > 0.1);
  protected readonly canZoomIn = computed(() => this.zoomValue() < 30);

  public constructor() {
    effect((onCleanup) => {
      const element = this.frame()?.nativeElement;
      if (!element || typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(() => untracked(() => this.narrow.set(isExcalidrawMobile(element.clientWidth, element.clientHeight))));
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });
    inject(DestroyRef).onDestroy(() => this.cancelFit());
    effect(() => {
      const scene = this.scene();
      const objects = this.objectCount();
      const frames = this.frameCount();
      untracked(() => this.counts.emit(scene ? { objects, frames } : null));
    });
    effect(() => {
      const id = this.boardId();
      untracked(() => void this.load(id));
    });
  }

  private async load(id: string): Promise<void> {
    const generation = ++this.generation;
    this.cancelFit();
    this.scene.set(null);
    this.failed.set(false);
    this.bridge = undefined;
    this.bridgeIsReady.set(false);
    this.zoomValue.set(1);
    this.presentFrames.set(null);
    try {
      const loaded = await this.api.read(id);
      if (generation !== this.generation) return;
      const checked = validateSceneImport(loaded.scene, { dropBadLinks: true });
      if ('error' in checked) throw new Error('The scene is not valid.');
      this.scene.set(checked.scene);
    } catch {
      if (generation === this.generation) this.failed.set(true);
    }
  }

  protected reload(): void {
    void this.load(this.boardId());
  }

  protected ready(bridge: CanvasBridge): void {
    this.bridge = bridge;
    this.bridgeIsReady.set(true);
    this.fitToContent(bridge);
  }

  /**
   * Prototype `BoardPanelRecord.tsx:50`: after layout, refresh and fit the live content (zoom factor .75, at most 1:1) so a scene far from the
   * origin still shows. Only the viewport moves: no scene write, and the editor's saved view is not touched. Cancelled on departure or board switch.
   */
  private fitToContent(bridge: CanvasBridge): void {
    this.cancelFit();
    // A second pass after the frame has surely been measured, so the scene is zoomed to fit even when the first ran before layout settled.
    const pass = (again: boolean): void => {
      this.fitTimer = setTimeout(() => {
        this.fitTimer = null;
        if (bridge.isDisposed || this.bridge !== bridge) return;
        fitBoardToContent(bridge);
        if (again) pass(false);
      }, again ? FIT_DELAY_MS : 3 * FIT_DELAY_MS);
    };
    pass(true);
  }

  private cancelFit(): void {
    if (this.fitTimer !== null) clearTimeout(this.fitTimer);
    this.fitTimer = null;
  }

  /** The engine reports its view on every change; the percentage follows it (R10-CX-260). */
  protected canvasChanged(change: { view: { zoom: number } }): void {
    this.zoomValue.set(change.view.zoom);
  }

  private engine(): Parameters<typeof zoomPreviewTo>[0] {
    return this.bridge && !this.bridge.isDisposed ? (this.bridge.excalidrawApi as Parameters<typeof zoomPreviewTo>[0]) : undefined;
  }

  /** The engine's own zoomIn / zoomOut: one `ZOOM_STEP` (0.1) added or taken away. */
  protected zoomStep(direction: 1 | -1): void {
    zoomPreviewTo(this.engine(), normalizeZoom(this.zoomValue() + direction * ZOOM_STEP));
  }

  /** The engine's own resetZoom button (the percentage): back to 100%. */
  protected zoomReset(): void {
    zoomPreviewTo(this.engine(), 1);
  }

  protected present(): void {
    const frames = presentableFrames(this.bridge);
    if (frames.length) this.presentFrames.set(frames);
  }

  protected endPresentation(): void {
    this.presentFrames.set(null);
  }
}
